import {
    colorBaseGreyWarm300,
    layoutGutterSmall,
    layoutMarginSmall,
    Link,
    ProductButton,
    ProductParagraph,
} from '@issuu/silkscreen';
import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';

import { toggleSubscribePublisher } from '../../api';
import { breakpointWidths } from '../../constants';
import DocumentContext from '../DocumentPage/DocumentContext';
import { ScreenReaderSpan } from '../ScreenReaderOnly';
import { checkLogin } from '../shared/check-login';
import { PublishDate } from './PublishDate';

const PublisherInfo = styled.div`
    width: max-content;
    max-width: 100%;
    display: grid;
    gap: calc(${layoutGutterSmall} * 0.5) ${layoutGutterSmall};
    margin-top: calc(${layoutMarginSmall} * 2);
    grid-template-columns: 3rem 1fr;
    align-items: center;
    cursor: pointer;

    @media (max-width: ${breakpointWidths.smallMax}px) {
        margin-top: ${layoutMarginSmall};
    }
`;

const PublisherLogo = styled.img`
    border-radius: 50%;
    height: 3rem;
    width: 3rem;
    margin-right: ${layoutMarginSmall};
    border: 1px solid ${colorBaseGreyWarm300};
    grid-row: span 2;
`;

export const PublisherDetails: React.FC<{ isLarge?: boolean }> = ({
    isLarge,
}: {
    isLarge?: boolean;
}) => {
    const {
        document: { documentName },
        hosts: { photo: photoHost },
        publisher,
        visitor,
    } = useContext(DocumentContext);
    const [subscriberCount, setSubscriberCount] = useState<number>(publisher.subscriberCount);
    const [isSubscribed, setIsSubscribed] = useState<boolean>(
        visitor.isLoggedIn ? visitor.isPublisherSubscribed : false,
    );

    const toggleSubscribe = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            checkLogin(visitor.isLoggedIn, () => {
                toggleSubscribePublisher(publisher.username, documentName, !isSubscribed).then(
                    rsp => {
                        setIsSubscribed(rsp.isPublisherSubscribed);
                        setSubscriberCount(rsp.subscriberCount);
                    },
                );
            });
        },
        [isSubscribed],
    );

    const linkProps = {
        onClick: (event: React.MouseEvent<HTMLAnchorElement | HTMLImageElement>) => {
            event.preventDefault();
            window.open(`/${publisher.username}`);
        },
        role: 'link',
    };

    return (
        <PublisherInfo itemProp="author">
            <PublisherLogo
                alt=""
                aria-hidden
                src={`//${photoHost}/${publisher.username}/photo_large.jpg`}
                {...linkProps}
            />
            <ProductParagraph step="negative-1">
                <Link href={`/${publisher.username}`} {...linkProps}>
                    {publisher.displayName}
                </Link>
            </ProductParagraph>
            {isLarge ? (
                <ProductParagraph step="negative-1">
                    <ProductButton onClick={toggleSubscribe} size="small" variant="tertiary">
                        {isSubscribed ? (
                            <>UnFollow</>
                        ) : (
                            <>
                                Follow{' '}
                                <ScreenReaderSpan>
                                    this publisher - current follower count:
                                </ScreenReaderSpan>
                                {subscriberCount}
                            </>
                        )}
                    </ProductButton>
                </ProductParagraph>
            ) : (
                <PublishDate />
            )}
        </PublisherInfo>
    );
};
